import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from './supabase';
import { applyEffectiveAccent } from './accent';
import type { Profile } from './types';

/**
 * Session + profile context.
 *
 * Single source of truth: one getSession() call at app mount, one
 * onAuthStateChange subscription. Components read via useSession() which
 * is just a context hook - no per-component getSession fetch, which
 * eliminates the GoTrue lock contention that used to freeze the app
 * when multiple components mounted simultaneously.
 */

const IMP_DEV_SESSION_KEY = 'cc:impersonation:dev-session';
const IMP_ACTIVE_KEY = 'cc:impersonation:active';

export interface ImpersonationState {
  active: boolean;
  session_id: string | null;
  target_display_name: string | null;
}

export function readImpersonationState(): ImpersonationState {
  if (typeof window === 'undefined') return { active: false, session_id: null, target_display_name: null };
  try {
    const raw = window.sessionStorage.getItem(IMP_ACTIVE_KEY);
    if (!raw) return { active: false, session_id: null, target_display_name: null };
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return { active: false, session_id: null, target_display_name: null };
  }
}

export function setImpersonationState(state: ImpersonationState) {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(IMP_ACTIVE_KEY, JSON.stringify(state));
}

export function stashDeveloperSession(payload: { access_token: string; refresh_token: string }) {
  window.sessionStorage.setItem(IMP_DEV_SESSION_KEY, JSON.stringify(payload));
}

export function popDeveloperSession(): { access_token: string; refresh_token: string } | null {
  const raw = window.sessionStorage.getItem(IMP_DEV_SESSION_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(IMP_DEV_SESSION_KEY);
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export async function signOut() {
  // Race supabase.signOut against a 4s timeout so a hung auth call can't
  // wedge the UI. We force-navigate regardless - belt + suspenders.
  try {
    await Promise.race([
      supabase.auth.signOut(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 4000)),
    ]);
  } catch (err) {
    console.warn('[session] supabase signOut failed / timed out', err);
  }
  // Forceful local cleanup. If supabase.signOut hangs, the SDK may not
  // have cleared its own storage - nuke everything we know it writes.
  try {
    window.sessionStorage.removeItem(IMP_ACTIVE_KEY);
    window.sessionStorage.removeItem(IMP_DEV_SESSION_KEY);
    // Clear every sb-*-auth-token / sb-*-code-verifier.
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith('sb-')) window.localStorage.removeItem(k);
    }
  } catch {}
  // Full navigation (not SPA) so SessionProvider re-initialises fresh.
  window.location.assign('/sign-in');
}

export interface SessionState {
  loading: boolean;
  isAuthed: boolean;
  profile: Profile | null;
  impersonation: ImpersonationState;
}

const DEFAULT_STATE: SessionState = {
  loading: true,
  isAuthed: false,
  profile: null,
  impersonation: { active: false, session_id: null, target_display_name: null },
};

const SessionContext = createContext<SessionState>(DEFAULT_STATE);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>(() => ({
    ...DEFAULT_STATE,
    impersonation: readImpersonationState(),
  }));

  useEffect(() => {
    let cancelled = false;
    // Sequence number guards against an older resolve() resolving after a
    // newer one has already written state. Each resolve() captures `seq`
    // before any await; if a newer resolve has bumped it past the captured
    // value, the older one drops its writes.
    let seq = 0;
    // Tracks the last user id we resolved for. Lets us skip redundant
    // resolves when rapid onAuthStateChange events fire for the same
    // session (common during PKCE exchange + initial sign-in).
    let lastUserId: string | null = null;
    let debounceTimer: number | null = null;

    async function resolve(reason: string) {
      const mySeq = ++seq;
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled || mySeq !== seq) return;
        const sess = data.session;
        if (!sess) {
          lastUserId = null;
          // Signed out - drop any personal pick, fall back to the global.
          applyEffectiveAccent(null);
          setState({
            loading: false,
            isAuthed: false,
            profile: null,
            impersonation: readImpersonationState(),
          });
          return;
        }

        // Skip the profile re-fetch if it's the same user we already have
        // and we're past the initial load. Rapid TOKEN_REFRESHED events
        // shouldn't re-pull the profile.
        if (lastUserId === sess.user.id && reason === 'auth-event') {
          return;
        }

        const { data: profileRow, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', sess.user.id)
          .maybeSingle();

        if (cancelled || mySeq !== seq) return;
        if (error) console.warn('[session] profile fetch failed', error);

        // Resolve the app-wide accent: personal pick wins, else the
        // admin global, else black-&-white. Cheap + idempotent - a
        // no-op repaint when the inline boot script already painted it.
        const prof = profileRow as Profile | null;
        applyEffectiveAccent(
          prof ? { accent: prof.accent ?? null, accent_strong: prof.accent_strong ?? null } : null,
        );

        lastUserId = sess.user.id;
        setState({
          loading: false,
          isAuthed: true,
          profile: prof,
          impersonation: readImpersonationState(),
        });
      } catch (err) {
        console.warn('[session] resolve failed', err);
        if (!cancelled && mySeq === seq) {
          setState((s) => ({ ...s, loading: false }));
        }
      }
    }

    // Initial resolve fires immediately.
    resolve('initial');

    // Auth event resolves are debounced 50ms so a burst (SIGNED_IN +
    // TOKEN_REFRESHED firing back to back) collapses into one resolve.
    const sub = supabase.auth.onAuthStateChange(() => {
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        resolve('auth-event');
      }, 50);
    });

    return () => {
      cancelled = true;
      if (debounceTimer !== null) window.clearTimeout(debounceTimer);
      sub.data.subscription.unsubscribe();
    };
  }, []);

  return createElement(SessionContext.Provider, { value: state }, children);
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}
